import { type ReactNode } from 'react';
import { EmptyState, Skeleton } from '@zafroi/ui-kit';
import { AlertTriangle } from 'lucide-react';

interface QueryStateGuardProps<T> {
  data: T | undefined | null;
  isLoading: boolean;
  isError: boolean;
  errorTitle?: string;
  errorDescription?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Primary CTA rendered inside the empty state — typically a <Button />. */
  emptyAction?: ReactNode;
  loadingSkeleton?: ReactNode;
  isEmpty?: (data: NonNullable<T>) => boolean;
  children: (data: NonNullable<T>) => ReactNode;
}

const defaultEmpty = <T,>(data: NonNullable<T>) => {
  if (Array.isArray(data)) return data.length === 0;
  return false;
};

export function QueryStateGuard<T>({
  data,
  isLoading,
  isError,
  errorTitle = "We couldn't load this just yet",
  errorDescription = 'Try refreshing — if it persists, the runtime might be unreachable.',
  emptyTitle = 'Nothing to show yet',
  emptyDescription,
  emptyAction,
  loadingSkeleton,
  isEmpty,
  children,
}: QueryStateGuardProps<T>) {
  if (isLoading) {
    return (
      <>
        {loadingSkeleton ?? (
          <div className="space-y-2.5">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}
      </>
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title={errorTitle}
        description={errorDescription}
      />
    );
  }

  if (data == null || (isEmpty ?? defaultEmpty)(data as NonNullable<T>)) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  return <>{children(data as NonNullable<T>)}</>;
}
